import { customToast } from "@/components/Common/Toast";
import { updateUserAccount } from "@/services/user";
import { RootState } from "@/store";
import { setIsDefaultDateOfBirth } from "@/store/userSlice";
import { calculateAge } from "@/utils/generalUtilities";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMinimumAge } from "../constants";
import { UserBenefitData } from "../types";

interface UseDateOfBirthValidationProps {
  userData: UserBenefitData;
  onSuccess: () => void;
}

export const useDateOfBirthValidation = ({
  userData,
  onSuccess,
}: UseDateOfBirthValidationProps) => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const [dateOfBirth, setDateOfBirth] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const validateAndSubmit = async () => {
    try {
      if (!dateOfBirth) {
        customToast("Please enter your date of birth to continue", "error");
        return;
      }

      setIsSubmitting(true);
      const userAge = calculateAge(dateOfBirth);

      const minimumAge = getMinimumAge();
      if (userAge < minimumAge) {
        customToast(
          `Please note that to qualify for SEP, you must be over ${minimumAge} years`,
          "error",
        );
        return;
      }

      const { FirstName, MiddleName, LastName, Telephone, EmailAddress } =
        userData;
      const response = await updateUserAccount(
        user.id,
        FirstName,
        MiddleName,
        LastName,
        dateOfBirth,
        Telephone,
        EmailAddress,
      );

      if (response.status === 200) {
        dispatch(setIsDefaultDateOfBirth(false));
        customToast("Successfully updated your date of birth", "success");
        onSuccess();
      } else {
        throw new Error("Failed to update account");
      }
    } catch {
      customToast("Failed to update date of birth", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    dateOfBirth,
    setDateOfBirth,
    isSubmitting,
    validateAndSubmit,
  };
};
